import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import LandingHeader from './common/LandingHeader';
import LandingFooter from './common/LandingFooter';
import './LandingPage.css';
import './Pricing.css';

const Pricing = () => {
  const { t } = useLanguage();
  const { isAuthenticated, currentUser } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [inquiryPlan, setInquiryPlan] = useState(null);
  const [inquiryCompanyInfo, setInquiryCompanyInfo] = useState('');
  const [inquirySending, setInquirySending] = useState(false);
  const [inquirySuccess, setInquirySuccess] = useState(false);
  const [inquiryError, setInquiryError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await axios.get('/api/subscriptions/plans');
        setPlans(response.data.plans || []);
      } catch (error) {
        console.error('Error fetching subscription plans:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const handleSelectPlan = (plan) => {
    if (!isAuthenticated) {
      navigate('/register');
    } else {
      setInquiryPlan(plan);
      setInquiryCompanyInfo('');
      setInquirySuccess(false);
      setInquiryError('');
    }
  };

  const handleCloseInquiry = () => {
    setInquiryPlan(null);
    setInquiryCompanyInfo('');
    setInquirySending(false);
    setInquirySuccess(false);
    setInquiryError('');
  };

  const handleSendInquiry = async () => {
    setInquirySending(true);
    setInquiryError('');
    try {
      await axios.post('/api/subscriptions/inquiry', {
        planName: inquiryPlan.display_name,
        billingCycle,
        companyInfo: inquiryCompanyInfo
      });
      setInquirySuccess(true);
    } catch (error) {
      console.error('Error sending inquiry:', error);
      setInquiryError(t('pricing.inquiryError'));
    } finally {
      setInquirySending(false);
    }
  };

  const formatPrice = (price) => {
    const numPrice = parseFloat(price);
    if (isNaN(numPrice)) return '0.00';
    return numPrice.toFixed(0);
  };

  return (
    <div className="landing-page">
      <LandingHeader />

      <section className="pricing-section">
        <div className="landing-container">
          <div className="pricing-header">
            <h1>{t('pricing.title')}</h1>
            <p className="pricing-subtitle">{t('pricing.subtitle')}</p>
            
            <div className="billing-toggle">
              <button
                className={billingCycle === 'monthly' ? 'active' : ''}
                onClick={() => setBillingCycle('monthly')}
              >
                {t('pricing.monthly')}
              </button>
              <button
                className={billingCycle === 'yearly' ? 'active' : ''}
                onClick={() => setBillingCycle('yearly')}
              >
                {t('pricing.yearly')}
                <span className="save-badge">{t('pricing.save20')}</span>
              </button>
            </div>
          </div>

          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>{t('common.loading')}...</p>
            </div>
          ) : (
            <div className="pricing-grid">
              {plans.map((plan, index) => (
                <div key={plan.id} className={`pricing-card ${index === 1 ? 'featured' : ''}`}>
                  {index === 1 && (
                    <div className="pricing-badge">{t('pricing.mostPopular')}</div>
                  )}
                  <div className="pricing-card-header">
                    <h3>{plan.display_name}</h3>
                    <div className="pricing-price">
                      <span className="currency">€</span>
                      <span className="amount">
                        {formatPrice(billingCycle === 'monthly' ? plan.price_monthly : plan.price_yearly)}
                      </span>
                      <span className="period">
                        /{billingCycle === 'monthly' ? t('pricing.month') : t('pricing.year')}
                      </span>
                    </div>
                    {billingCycle === 'yearly' && (
                      <p className="billing-note">
                        {t('pricing.billedYearly')}
                      </p>
                    )}
                  </div>
                  
                  <div className="pricing-features">
                    <ul>
                      {(typeof plan.features === 'string' 
                        ? JSON.parse(plan.features) 
                        : plan.features || []
                      ).map((feature, featureIndex) => {
                        // Handle both old string format and new object format
                        let displayText;
                        if (typeof feature === 'object' && feature.key) {
                          // New format with translation key
                          const translationKey = `pricing.features.${feature.key}`;
                          displayText = feature.value 
                            ? t(translationKey, translationKey, { value: feature.value })
                            : t(translationKey, translationKey);
                        } else {
                          // Old format (plain string) - fallback
                          displayText = feature;
                        }
                        
                        return (
                          <li key={featureIndex}>
                            <span className="pricing-feature-icon">✓</span>
                            {displayText}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                  
                  <div className="pricing-action">
                    <button
                      className={`pricing-button ${index === 1 ? 'primary' : 'secondary'}`}
                      onClick={() => handleSelectPlan(plan)}
                    >
                      {isAuthenticated ? t('pricing.sendInquiry') : t('pricing.selectPlan')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="pricing-faq">
            <h2>{t('pricing.faqTitle')}</h2>
            <div className="faq-grid">
              <div className="faq-item">
                <h4>{t('pricing.faq1Question')}</h4>
                <p>{t('pricing.faq1Answer')}</p>
              </div>
              <div className="faq-item">
                <h4>{t('pricing.faq2Question')}</h4>
                <p>{t('pricing.faq2Answer')}</p>
              </div>
              <div className="faq-item">
                <h4>{t('pricing.faq3Question')}</h4>
                <p>{t('pricing.faq3Answer')}</p>
              </div>
              <div className="faq-item">
                <h4>{t('pricing.faq4Question')}</h4>
                <p>{t('pricing.faq4Answer')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <LandingFooter />

      {inquiryPlan && (
        <div className="inquiry-overlay" onClick={handleCloseInquiry}>
          <div className="inquiry-modal" onClick={e => e.stopPropagation()}>
            {inquirySuccess ? (
              <div className="inquiry-success">
                <div className="inquiry-success-icon">✓</div>
                <p>{t('pricing.inquirySent')}</p>
                <button className="pricing-button primary" onClick={handleCloseInquiry}>
                  OK
                </button>
              </div>
            ) : (
              <>
                <h3>{t('pricing.inquiryTitle')}</h3>
                <div className="inquiry-message">
                  <p>
                    {t('pricing.inquiryMessage', 'pricing.inquiryMessage', {
                      plan: `${inquiryPlan.display_name} – ${billingCycle}`,
                      name: currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : '',
                      email: currentUser?.email || ''
                    })}
                  </p>
                </div>
                <label className="inquiry-label">{t('pricing.companyInfo')}</label>
                <textarea
                  className="inquiry-textarea"
                  value={inquiryCompanyInfo}
                  onChange={e => setInquiryCompanyInfo(e.target.value)}
                  placeholder={t('pricing.companyInfoPlaceholder')}
                  rows={4}
                />
                {inquiryError && <p className="inquiry-error">{inquiryError}</p>}
                <div className="inquiry-actions">
                  <button className="pricing-button secondary" onClick={handleCloseInquiry} disabled={inquirySending}>
                    {t('common.cancel')}
                  </button>
                  <button className="pricing-button primary" onClick={handleSendInquiry} disabled={inquirySending}>
                    {inquirySending ? t('pricing.inquirySending') : t('pricing.sendInquiry')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Pricing;

